import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Checkbox, Divider, Form, Input } from 'antd';
import { CardBadge } from './CardBadge';
import { useStepperContext } from './TariffCreationModal/context';
import { useCompaniesChildrenSelect, useCompaniesSelect, useTariffsPackages } from '../../../hooks';
import { apiMessagesGetFormRules, getCompaniesChildrenSelect } from '../../../middleware';
import { makeSelectionLabel, tAdvancedPlural } from '../../../instruments';
import { tB, tF, tI, tT } from '../../../translate';
import TariffsStyles from '../m_styles.less';

export const AssignmentStep = observer(() => {
    const searchString = 'company_';
    const companyList = useCompaniesChildrenSelect();
    const packages = useTariffsPackages();
    const [form] = Form.useForm();
    const { formData, updateFormData } = useStepperContext();
    const [searchText, setSearchText] = useState('');
    const ownerCompany = useCompaniesSelect().filter((value) => value.id == formData.companyId)?.[0];

    useEffect(() => {
        if (formData.companyId) {
            getCompaniesChildrenSelect(formData.companyId, true);
        }
    }, [formData.companyId]);

    useEffect(() => {
        const initialValues: { [key: string]: boolean } = {};
        companyList.forEach((company) => {
            initialValues[`${searchString}${company.id}`] = formData.companyIds.includes(Number(company.id));
        });
        form.setFieldsValue(initialValues);
    }, [form, formData.companyIds, companyList, formData.companyId]);

    const handleValuesChange = (_: any, allValues: any) => {
        const selectedCompanyIds: number[] = [];
        Object.keys(allValues).forEach((key) => {
            if (key.startsWith(searchString) && allValues[key]) {
                const companyId = key.replace(searchString, '');
                selectedCompanyIds.push(Number(companyId));
            }
        });

        updateFormData({ companyIds: selectedCompanyIds });
    };

    const getSelectedPackages = () => {
        return packages.filter((pkg) => formData.tariffPackageIds.includes(Number(pkg.id)));
    };

    const filteredCompanyList = companyList.filter((company) =>
        company.name.toLowerCase().includes(searchText.toLowerCase()),
    );

    const selectedPackages = getSelectedPackages();
    const localPackages = selectedPackages.filter((pkg) => pkg.packageType === 'local');
    const roamingPackages = selectedPackages.filter((pkg) => pkg.packageType !== 'local');
    const selectedCompanies = companyList.filter((company) => formData.companyIds.includes(Number(company.id)));

    return (
        <div className={TariffsStyles.assignmentStep}>
            <div className={TariffsStyles.assignmentHeader}>
                <h3 className={TariffsStyles.header3}> {tF('Assign to customer Companies')} </h3>
                <p className={TariffsStyles.description}>{tF('Select which customer companies can use this tariff')}</p>
            </div>
            <Input
                placeholder={tF('Search companies')}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className={TariffsStyles.searchInput}
                allowClear
            />
            <div className={TariffsStyles.companiesContainer}>
                <Form form={form} onValuesChange={handleValuesChange} className={'formStyle'}>
                    {filteredCompanyList.length > 0 ? (
                        filteredCompanyList.map((item) => (
                            <div key={item.id}>
                                <Form.Item
                                    name={`${searchString}${item.id}`}
                                    valuePropName="checked"
                                    rules={apiMessagesGetFormRules}
                                    className={TariffsStyles.checkboxFormItem}
                                >
                                    <Checkbox>{item.name}</Checkbox>
                                </Form.Item>
                            </div>
                        ))
                    ) : (
                        <div>
                            {tT('No companies found for')} {ownerCompany?.name}
                        </div>
                    )}
                </Form>
            </div>
            <Divider />
            <h3>{tF('Summary')}</h3>
            <div className={TariffsStyles.summaryContainer}>
                <div className={TariffsStyles.summaryItem}>
                    <div className={TariffsStyles.itemTitle}>{tF('Tariff Name')}:</div>
                    <div className={TariffsStyles.itemsContent}>
                        {formData.name ? (
                            <div className={TariffsStyles.badge}>
                                <CardBadge name={formData.name} />
                            </div>
                        ) : (
                            <span className={TariffsStyles.noItems}>{tI('Not set')}</span>
                        )}
                    </div>
                </div>
                <div className={TariffsStyles.summaryItem}>
                    <div className={TariffsStyles.itemTitle}>{tF('Price')}:</div>
                    <div className={TariffsStyles.itemsContent}>
                        {!!formData.priceCents && formData.priceCents > 0 ? (
                            <div className={TariffsStyles.badge}>
                                <CardBadge name={`€${(formData.priceCents / 100).toFixed(2)}`} />
                            </div>
                        ) : (
                            <span className={TariffsStyles.noItems}>{tI('Not set')}</span>
                        )}
                    </div>
                </div>
                <div className={TariffsStyles.summaryItem}>
                    <div className={TariffsStyles.itemTitle}>{tF('Owner')}:</div>
                    <div className={TariffsStyles.itemsContent}>
                        {ownerCompany ? (
                            <div className={TariffsStyles.badge}>
                                <CardBadge name={ownerCompany.name} />
                            </div>
                        ) : (
                            <span className={TariffsStyles.noItems}>{tI('Not set')}</span>
                        )}
                    </div>
                </div>
                <div className={TariffsStyles.summaryItem}>
                    <div className={TariffsStyles.itemTitle}>
                        {tAdvancedPlural('local_package', localPackages?.length)}:
                    </div>
                    <div className={TariffsStyles.itemsContent}>
                        {localPackages.length > 0 ? (
                            localPackages.map((pkg) => (
                                <div key={pkg.id} className={TariffsStyles.badge}>
                                    <CardBadge name={pkg.name || `${tF('Package')} ${pkg.id}`} />
                                </div>
                            ))
                        ) : (
                            <span className={TariffsStyles.noItems}>{tI('None selected')}</span>
                        )}
                    </div>
                </div>
                <div className={TariffsStyles.summaryItem}>
                    <div className={TariffsStyles.itemTitle}>
                        {tAdvancedPlural('roaming_package', roamingPackages?.length)}:
                    </div>
                    <div className={TariffsStyles.itemsContent}>
                        {roamingPackages.length > 0 ? (
                            <>
                                <div className={TariffsStyles.badge}>
                                    <CardBadge name={makeSelectionLabel('package', roamingPackages.length)} />
                                </div>
                                <div className={TariffsStyles.itemDescription}>
                                    {roamingPackages.map((pkg) => pkg.name || `${tF('Package')} ${pkg.id}`).join(', ')}
                                </div>
                            </>
                        ) : (
                            <span className={TariffsStyles.noItems}>{tI('None selected')}</span>
                        )}
                    </div>
                </div>
                <div className={TariffsStyles.summaryItem}>
                    <div className={TariffsStyles.itemTitle}>{tF('Assigned Companies')}:</div>
                    <div className={TariffsStyles.itemsContent}>
                        {selectedCompanies.length > 0 ? (
                            <>
                                <div className={TariffsStyles.badge}>
                                    <CardBadge name={makeSelectionLabel('company', selectedCompanies.length)} />
                                </div>
                                <div className={TariffsStyles.itemDescription}>
                                    {selectedCompanies.map((company) => company.name.trim()).join(', ')}
                                </div>
                            </>
                        ) : (
                            <span className={TariffsStyles.noItems}>{tI('None selected')}</span>
                        )}
                    </div>
                </div>
                {formData.outOfBundle && (
                    <div className={TariffsStyles.summaryItem}>
                        <div>{tI('Out of Bundle')}:</div>
                        <div>
                            <CardBadge name={tB('Yes')} />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
});
